// @ts-check
import { observeImage } from "../../platform/assets.js";

// Measured rectangles in the supplied 1254px sheet. Rows are not uniform:
// the raised jump arm begins above its nominal cell, and crouches are shorter.
// Each rectangle has a shared foot baseline and a measured horizontal anchor.
export const SPRITE_SIZE = { sheet: 1254, width: 304, height: 306 };
export const POSES = [
  { x: 82, y: 18, width: 176, height: 289, anchor: 172 },
  { x: 383, y: 17, width: 176, height: 290, anchor: 472 },
  { x: 683, y: 17, width: 210, height: 289, anchor: 789 },
  { x: 1017, y: 18, width: 182, height: 288, anchor: 1108 },
  { x: 57, y: 329, width: 197, height: 287, anchor: 158 },
  { x: 361, y: 331, width: 224, height: 284, anchor: 479 },
  { x: 680, y: 330, width: 219, height: 282, anchor: 790 },
  { x: 985, y: 330, width: 244, height: 278, anchor: 1106 },
  { x: 44, y: 641, width: 242, height: 282, anchor: 170 },
  { x: 361, y: 641, width: 234, height: 282, anchor: 481 },
  { x: 670, y: 692, width: 233, height: 231, anchor: 800 },
  { x: 985, y: 614, width: 238, height: 285, anchor: 1110 },
  { x: 38, y: 929, width: 256, height: 280, anchor: 174 },
  { x: 348, y: 941, width: 249, height: 281, anchor: 485 },
  { x: 672, y: 1003, width: 245, height: 221, anchor: 802 },
];
// Separately drawn left-facing artwork: normal Q/P shells, left-leading limbs.
// Its last row contains crouch, takeoff, and open-arm flight poses, so remap
// those source cells to the same animation states as the original right sheet.
export const LEFT_POSES = [
  { x: 96, y: 31, width: 161, height: 267, anchor: 174 },
  { x: 385, y: 31, width: 160, height: 267, anchor: 464 },
  { x: 647, y: 47, width: 237, height: 255, anchor: 760 },
  { x: 951, y: 53, width: 225, height: 251, anchor: 1053 },
  { x: 54, y: 355, width: 220, height: 246, anchor: 155 },
  { x: 353, y: 360, width: 219, height: 246, anchor: 451 },
  { x: 655, y: 363, width: 223, height: 241, anchor: 749 },
  { x: 951, y: 362, width: 212, height: 247, anchor: 1037 },
  { x: 58, y: 662, width: 210, height: 238, anchor: 149 },
  { x: 355, y: 658, width: 207, height: 230, anchor: 447 },
  { x: 59, y: 996, width: 203, height: 200, anchor: 144 },
  { x: 360, y: 938, width: 216, height: 221, anchor: 445 },
  { x: 631, y: 925, width: 248, height: 203, anchor: 741 },
  { x: 631, y: 925, width: 248, height: 203, anchor: 741 },
  { x: 59, y: 996, width: 203, height: 200, anchor: 144 },
];
export const LEFT_SCALE = 1.1;
const WALK = [2, 3, 4, 5, 6, 7, 8, 9];
// At 176px tall, one stride travels 136px. Smaller QP takes shorter steps.
const PIXELS_PER_STEP = 17;

/** @typedef {{ distance: number, facing: number, landing: number }} Animation */

/**
 * Choose a pose from movement, not from key repeat or the display refresh rate.
 * @param {{ y: number, vy: number, vx: number, grounded?: boolean }} position
 * @param {Animation} animation
 * @param {number} elapsed
 * @param {number} traveled
 * @param {boolean} wasAirborne
 * @param {number} [characterHeight]
 */
export function animate(position, animation, elapsed, traveled, wasAirborne, characterHeight = 176) {
  const moving = Math.abs(traveled) > 0.01 && Math.abs(position.vx) > 0.1;
  if (moving) animation.facing = Math.sign(traveled);
  animation.distance = moving ? animation.distance + Math.abs(traveled) * 176 / Math.max(1, characterHeight) : 0;
  animation.landing = Math.max(0, animation.landing - elapsed);
  const grounded = position.grounded ?? (position.y === 0 && position.vy === 0);
  if (wasAirborne && grounded) animation.landing = 0.09;
  if (!grounded) {
    if (position.vy > 80) return 11;
    if (position.vy > -160) return 12;
    return 13;
  }
  if (moving) return WALK[Math.floor(animation.distance / PIXELS_PER_STEP) % WALK.length];
  return animation.landing > 0 ? 14 : 0;
}

/** Select existing artwork for deliberate tricks while keeping Q/P upright.
 * @param {import("../../game/player.js").Position} position
 * @param {number} ordinaryPose
 */
export function trickPose(position, ordinaryPose) {
  if (position.impact > 0) return 14;
  if (position.pound !== "none" || position.crouching || position.sliding) return 10;
  if (position.boost > 0) return 12;
  return ordinaryPose;
}

/**
 * Choose directional artwork and register every pose to the feet without flipping.
 * @param {HTMLElement} viewport
 * @param {HTMLImageElement} image
 * @param {number} index
 * @param {number} facing
 */
export function renderPose(viewport, image, index, facing) {
  const isLeft = facing < 0;
  const pose = (isLeft ? LEFT_POSES : POSES)[index];
  const scale = isLeft ? LEFT_SCALE : 1;
  // A page published under its own prefix names the folder that holds the sheets; everything else keeps ./images/.
  const base = image.dataset.poseBase ?? "./images/";
  // `data-pose-left` holds a transparent copy of the left sheet once a page has made one.
  const source = isLeft ? (image.dataset.poseLeft ?? `${base}qp-move-left.png`) : `${base}qp-move.png`;
  if (image.getAttribute("src") !== source) image.src = source;
  const left = SPRITE_SIZE.width / 2 + (pose.x - pose.anchor) * scale;
  // QP's head and torso are letters: preserve their orientation in both directions.
  viewport.dataset.frame = String(index);
  viewport.dataset.facing = facing < 0 ? "left" : "right";
  viewport.style.width = `${pose.width * scale / SPRITE_SIZE.width * 100}%`;
  viewport.style.height = `${pose.height * scale / SPRITE_SIZE.height * 100}%`;
  viewport.style.left = `${left / SPRITE_SIZE.width * 100}%`;
  viewport.style.transform = "none";
  image.style.width = `${SPRITE_SIZE.sheet / pose.width * 100}%`;
  image.style.height = `${SPRITE_SIZE.sheet / pose.height * 100}%`;
  image.style.left = `${-pose.x / pose.width * 100}%`;
  image.style.top = `${-pose.y / pose.height * 100}%`;
}

/** Draw the existing directional poses with a transparent backdrop over scenery.
 * @param {HTMLCanvasElement} canvas
 * @param {HTMLImageElement} right @param {HTMLImageElement} left
 * @param {(source: HTMLImageElement) => HTMLImageElement | HTMLCanvasElement} clean
 * @param {() => void} onReady
 */
export function createPoseRenderer(canvas, right, left, clean, onReady) {
  const context = canvas.getContext("2d");
  /** @type {Map<number, HTMLImageElement | HTMLCanvasElement>} */
  const sources = new Map();
  const release = [right, left].map((source, side) => {
    const prepare = () => {
      if (!source.naturalWidth) return;
      sources.set(side, clean(source));
      onReady();
    };
    return observeImage(source, prepare);
  });
  return {
    dispose() { for (const cleanup of release) cleanup(); sources.clear(); },
    /** @param {number} index @param {number} facing */
    render(index, facing) {
      const isLeft = facing < 0;
      const source = sources.get(isLeft ? 1 : 0);
      if (!context || !source) return false;
      const pose = (isLeft ? LEFT_POSES : POSES)[index];
      const scale = isLeft ? LEFT_SCALE : 1;
      const x = SPRITE_SIZE.width / 2 + (pose.x - pose.anchor) * scale;
      const height = pose.height * scale;
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(source, pose.x, pose.y, pose.width, pose.height, x, SPRITE_SIZE.height - height, pose.width * scale, height);
      canvas.dataset.frame = String(index);
      canvas.dataset.facing = isLeft ? "left" : "right";
      return true;
    },
  };
}
